import { Body, Controller, Delete, Get, Param, Patch, Post, Query, UseGuards } from '@nestjs/common';
import { AuthGuard } from '@nestjs/passport';
import { ApiBearerAuth, ApiOperation, ApiTags } from '@nestjs/swagger';
import { RequireModule } from '../../auth/decorators/require-module.decorator';
import { RequirePermission } from '../../auth/decorators/require-permission.decorator';
import type { LoginUserInfo } from '../../auth/dto/login.dto';
import { GetUser } from '../../auth/get-user.decorator';
import { ModuleGuard } from '../../auth/guards/module.guard';
import { PermissionGuard } from '../../auth/guards/permission.guard';
import {
  CreateFormatoAcreditacionDto,
  QueryFormatosDto,
  UpdateFormatoAcreditacionDto,
} from '../dto/acreditaciones-bancarias.dto';
import { FormatosBancariosService } from '../services/formatos-bancarios.service';

@ApiTags('RRHH - Formatos bancarios (acreditación)')
@Controller({ path: 'rrhh/formatos-bancarios', version: '1' })
@UseGuards(AuthGuard('jwt'), ModuleGuard, PermissionGuard)
@RequireModule('RRHH')
@ApiBearerAuth()
export class FormatosBancariosController {
  constructor(private readonly service: FormatosBancariosService) {}

  @Get()
  @RequirePermission('RRHH', 'RH_BAN_ACREDITACION_VER')
  @ApiOperation({ summary: 'Listar formatos configurados por banco' })
  listar(@GetUser() user: LoginUserInfo, @Query() query: QueryFormatosDto) {
    return this.service.listar(user.empresa_id, query);
  }

  @Get(':id')
  @RequirePermission('RRHH', 'RH_BAN_ACREDITACION_VER')
  @ApiOperation({ summary: 'Obtener formato' })
  obtener(@Param('id') id: string, @GetUser() user: LoginUserInfo) {
    return this.service.obtener(id, user.empresa_id);
  }

  @Post()
  @RequirePermission('RRHH', 'RH_BAN_FORMATO_EDITAR')
  @ApiOperation({
    summary: 'Crear formato bancario',
    description:
      'Sintaxis de templates: {{campo|filtro:arg|filtro:arg}}. ' +
      'Campos disponibles en detalle: cedula, ruc, numero_empleado, numero_ips, nombres, apellidos, ' +
      'nombre_completo, cuenta_bancaria, tipo_cuenta, monto, monto_centavos, indice. ' +
      'Filtros: upper, lower, trim, noaccent, trunc:N, padl:N, padr:N, pad0:N, num:N, replace:from:to, default:VAL.',
  })
  crear(@GetUser() user: LoginUserInfo, @Body() dto: CreateFormatoAcreditacionDto) {
    return this.service.crear(user.empresa_id, dto);
  }

  @Patch(':id')
  @RequirePermission('RRHH', 'RH_BAN_FORMATO_EDITAR')
  @ApiOperation({ summary: 'Editar formato' })
  actualizar(
    @Param('id') id: string,
    @GetUser() user: LoginUserInfo,
    @Body() dto: UpdateFormatoAcreditacionDto,
  ) {
    return this.service.actualizar(id, user.empresa_id, dto);
  }

  @Delete(':id')
  @RequirePermission('RRHH', 'RH_BAN_FORMATO_EDITAR')
  @ApiOperation({ summary: 'Eliminar formato (solo si no fue usado)' })
  eliminar(@Param('id') id: string, @GetUser() user: LoginUserInfo) {
    return this.service.eliminar(id, user.empresa_id);
  }
}
